import { useConversations } from '@cascade/react/conversations';
import type { TMessageFeedbackType } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import { useProjects } from '@cascade/react/projects';
import { Button, Icon, Text, Tooltip, useToast } from '@cascade/ui-library';
import { useRef, useState } from 'react';
import { FeedbackDialog } from './feedback-dialog/index.ts';
import { getFeedbackMark } from './ReplyControls.helpers.ts';
import classes from './ReplyControls.module.scss';
import type {
  TFeedbackFailure,
  TReplyControlsProps,
} from './ReplyControls.types.ts';
import { CreateTicketDialog } from '#components/create-ticket-dialog/index.ts';

export const ReplyControls = ({
  messageId,
  text,
  feedback,
  feedbackMessage,
}: TReplyControlsProps) => {
  const { translate, translateError } = useI18n();
  const { currentConversation, canEscalateConversation, sendMessageFeedback } =
    useConversations();

  const { settings } = useProjects();
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [feedbackType, setFeedbackType] = useState<TMessageFeedbackType>();
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [failure, setFailure] = useState<TFeedbackFailure>();
  const isSendingRef = useRef(false);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  const feedbackMark = getFeedbackMark(feedback);
  const feedbackRules = settings?.feedback_message_rules;
  const conversationId = currentConversation?.conversation_id;
  const createTicketLabel =
    settings?.hr_support_button_text || translate('ticket.create');

  const failureMessage = failure ? translateError(failure.error) : undefined;

  const dialogError =
    failure?.type === feedbackType ? failureMessage : undefined;

  const inlineError =
    failure && failure.type !== feedbackType ? failureMessage : undefined;

  const sendFeedback = async (
    type: TMessageFeedbackType,
    reason?: string,
  ): Promise<boolean> => {
    if (isSendingRef.current) {
      return false;
    }

    isSendingRef.current = true;
    setIsSending(true);
    setFailure(undefined);

    try {
      await sendMessageFeedback({
        message_id: messageId,
        type,
        feedback_message: reason,
      });

      copyButtonRef.current?.focus();

      return true;
    } catch (error) {
      setFailure({ type, error });

      return false;
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const rateReply = (type: TMessageFeedbackType) => {
    if (feedbackRules?.[type] === 'none') {
      sendFeedback(type);

      return;
    }

    setFeedbackType(type);
  };

  const likeReply = () => rateReply('like');

  const dislikeReply = () => rateReply('dislike');

  const closeFeedback = () => setFeedbackType(undefined);

  const openCreateTicket = () => setIsCreateTicketOpen(true);

  const closeCreateTicket = () => setIsCreateTicketOpen(false);

  const sendReason = async (type: TMessageFeedbackType, reason: string) => {
    const isSent = await sendFeedback(type, reason);

    if (isSent) {
      setFeedbackType(undefined);
    }
  };

  const copyReply = async () => {
    try {
      await navigator.clipboard.writeText(text);

      const title = translate('agent.reply.copied');

      toast({ title, variant: 'success' });
    } catch {
      const title = translate('agent.reply.copyFailed');

      toast({ title, variant: 'danger' });
    }
  };

  const markContent = feedbackMark ? (
    <div className={classes.mark}>
      <Icon type={feedbackMark.iconType} color={feedbackMark.color} size='sm' />
      <Text
        type='caption'
        color={feedbackMark.color}
        weight='medium'
        content={translate(feedbackMark.labelKey)}
      />
    </div>
  ) : null;

  const markNode = feedbackMessage ? (
    <Tooltip content={feedbackMessage} placement='top'>
      {markContent}
    </Tooltip>
  ) : (
    markContent
  );

  return (
    <div className={classes['reply-controls']}>
      {feedbackMark ? (
        markNode
      ) : (
        <>
          <Button
            iconType='thumbs-up'
            label={translate('agent.reply.like')}
            iconOnly
            ghost
            disabled={isSending}
            onClick={likeReply}
          />
          <Button
            iconType='thumbs-down'
            label={translate('agent.reply.dislike')}
            iconOnly
            ghost
            disabled={isSending}
            onClick={dislikeReply}
          />
        </>
      )}
      <Button
        ref={copyButtonRef}
        iconType='copy'
        label={translate('agent.reply.copy')}
        iconOnly
        ghost
        onClick={copyReply}
      />
      {canEscalateConversation ? (
        <Button
          iconType='ticket'
          label={createTicketLabel}
          ghost
          onClick={openCreateTicket}
        />
      ) : null}
      {inlineError ? (
        <Text
          type='caption'
          color='text-danger'
          role='alert'
          content={inlineError}
        />
      ) : null}
      {feedbackType ? (
        <FeedbackDialog
          type={feedbackType}
          isReasonRequired={feedbackRules?.[feedbackType] === 'required'}
          isSending={isSending}
          errorMessage={dialogError}
          onSubmit={(reason) => sendReason(feedbackType, reason)}
          onClose={closeFeedback}
        />
      ) : null}
      {isCreateTicketOpen && conversationId ? (
        <CreateTicketDialog
          conversationId={conversationId}
          messageId={messageId}
          onClose={closeCreateTicket}
        />
      ) : null}
    </div>
  );
};
